import { clsx } from 'clsx';
import { ElementType, forwardRef, ReactNode } from 'react';
import {
  ButtonEdges,
  ButtonIconDirections,
  ButtonSizes,
  ButtonTypes,
  ButtonVariations
} from './models';
import { Icon } from '../icon/Icon';
import { getDataIdGenerator } from '../shared/utils/dataId';

export interface ButtonProps {
  dataId: string;
  tag?: ElementType;
  type?: ButtonTypes;
  icon?: string;
  iconClassName?: string;
  iconOnly?: boolean;
  iconSrc?: string;
  direction?: ButtonIconDirections;
  edge?: ButtonEdges;
  variation?: ButtonVariations;
  palette?: string;
  size?: ButtonSizes;
  active?: boolean;
  inlineText?: boolean;
  showProgress?: boolean;
  children?: ReactNode;
  actionRequiredIndicator?: boolean;
  className?: string;
  disabled?: boolean;
  onClick?: VoidFunction | undefined;
  [key: string]: string | number | boolean | symbol | object | null | undefined | never;
}

export const Button = forwardRef<ElementType, ButtonProps>(
  (
    {
      dataId,
      tag,
      type,
      icon,
      iconClassName = '',
      iconOnly,
      iconSrc,
      direction,
      edge,
      variation = 'solid',
      palette,
      size,
      inlineText,
      showProgress,
      actionRequiredIndicator,
      active,
      children,
      className,
      onClick,
      disabled,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const classNames = clsx([
      {
        'btn-primary': !palette && (variation === 'solid' || variation === 'faded'),
        'btn-secondary': !palette && variation === 'outline',
        btn: variation === 'solid',
        [`btn-${variation}`]: !!variation && variation !== 'solid',
        [`btn-edge-${edge}`]: !!edge,
        [`btn-${palette}`]: !!palette,
        'btn-md': !size,
        [`btn-${size}`]: !!size,
        'with-inline-text': inlineText,
        'in-progress': showProgress,
        'btn-action-required-indicator': actionRequiredIndicator,
        'btn-thumbnail': (icon || iconSrc) && iconOnly,
        'btn-thumbnail-left': (icon || iconSrc) && !iconOnly && !direction,
        [`btn-thumbnail-${direction}`]: (icon || iconSrc) && !iconOnly && direction,
        'btn-active': active
      },
      className
    ]);

    const iconClassesComputed = clsx(['thumb', iconClassName]);

    const ButtonTag = tag || 'button';
    const dataIdGenerator = getDataIdGenerator(dataId);

    return (
      <ButtonTag
        type={type || 'button'}
        data-id={dataIdGenerator(tag === 'a' ? 'link' : 'button')}
        className={classNames}
        ref={ref}
        onClick={() => {
          if (onClick) {
            onClick();
          }
        }}
        disabled={!!disabled}
        {...props}
      >
        {icon && direction !== 'right' ? (
          <Icon dataId={dataIdGenerator('icon')} name={icon} className={iconClassesComputed} />
        ) : null}

        {iconSrc && direction !== 'right' ? (
          <img src={iconSrc} data-id={dataIdGenerator('img')} className='thumb' alt='icon' />
        ) : null}

        {children}

        {icon && direction === 'right' ? (
          <Icon dataId={dataIdGenerator('icon')} name={icon} className={iconClassesComputed} />
        ) : null}

        {iconSrc && direction === 'right' ? (
          <img src={iconSrc} data-id={dataIdGenerator('img')} className='thumb' alt='icon' />
        ) : null}
      </ButtonTag>
    );
  }
);
